import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useCallback, useState } from 'react'

import { axiosInstance } from '../helpers'
import { FormUser } from '../models/Forms'

export interface SheetOnboardCreateRequest {
    organization: number
    property_name: string
    address: string
    city: string
    state: string
    zip_code: string
    billing_email?: string | null
    regional_manager: FormUser
    property_manager?: FormUser
    other_users?: FormUser[]
}

export interface AreaPreview {
    id: number
    name: string
    occupiable: boolean
}

export interface UnitAreaCount {
    name: string
    count: number
    occupiable?: boolean
}

export interface UnitConfigPreview {
    id: number
    name: string
    bed_count: number | null
    bath_count: number | null
    areas: UnitAreaCount[]
}

export interface FolderNode {
    id: number
    name: string
    children: FolderNode[]
    units: { id: number; name: string; unit_config: string | null }[]
}

export interface SheetPreview {
    areas: AreaPreview[]
    unit_configs: UnitConfigPreview[]
    folders: FolderNode[]
}

export interface SheetOnboardState {
    apartmentId: number | null
    formId: number | null
    company: any | null
    preview: SheetPreview
}

const emptyPreview: SheetPreview = {
    areas: [],
    unit_configs: [],
    folders: [],
}

export const useSheetOnboard = () => {
    const queryClient = useQueryClient()

    const [state, setState] = useState<SheetOnboardState>({
        apartmentId: null,
        formId: null,
        company: null,
        preview: emptyPreview,
    })

    const createMutation = useMutation({
        mutationFn: async (req: SheetOnboardCreateRequest) => {
            const { data } = await axiosInstance.post(
                'forms/sheet-onboard/',
                req,
            )
            return data
        },
        onSuccess: (data) => {
            setState({
                apartmentId: data.apartment?.id ?? null,
                formId: data.form_id ?? null,
                company: data.apartment ?? null,
                preview: emptyPreview,
            })
        },
        onError: (err: any) => {
            toast.error(
                err?.response?.data?.message || 'Failed to create property',
            )
        },
    })

    const uploadMutation = useMutation({
        mutationFn: async ({
            apartmentId,
            sheetType,
            file,
            organization,
        }: {
            apartmentId: number
            sheetType: 'area_sheet' | 'unit_sheet' | 'infrastructure_sheet'
            file: File
            organization: number
        }) => {
            const form = new FormData()
            form.append('file', file)
            form.append('sheet_type', sheetType)
            form.append('organization', String(organization))
            const {
                data,
            } = await axiosInstance.post(
                `forms/sheet-onboard/${apartmentId}/upload/`,
                form,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            )
            return data
        },
        onSuccess: (data) => {
            if (data.preview) {
                setState((s) => ({
                    ...s,
                    preview: data.preview,
                    company: data.company ?? s.company,
                }))
            }
            if (data.errors?.length) {
                toast.warn(
                    `Upload finished with ${data.errors.length} error(s).`,
                )
            }
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message || 'Upload failed')
        },
    })

    const resetMutation = useMutation({
        mutationFn: async ({
            apartmentId,
            organization,
        }: {
            apartmentId: number
            organization: number
        }) => {
            const {
                data,
            } = await axiosInstance.post(
                `forms/sheet-onboard/${apartmentId}/reset/`,
                { organization },
            )
            return data
        },
        onSuccess: (data) => {
            setState((s) => ({
                ...s,
                preview: data.preview ?? emptyPreview,
                company: data.company ?? s.company,
            }))
            toast.info('Sheets reset. You can re-upload.')
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message || 'Reset failed')
        },
    })

    const completeMutation = useMutation({
        mutationFn: async ({
            apartmentId,
            organization,
        }: {
            apartmentId: number
            organization: number
        }) => {
            const {
                data,
            } = await axiosInstance.post(
                `forms/sheet-onboard/${apartmentId}/complete/`,
                { organization },
            )
            return data
        },
        onSuccess: () => {
            toast.success('Onboarding complete!')
            queryClient.invalidateQueries({ queryKey: ['companies'] })
        },
        onError: (err: any) => {
            toast.error(
                err?.response?.data?.message || 'Failed to complete onboarding',
            )
        },
    })

    const reset = useCallback(() => {
        setState({
            apartmentId: null,
            formId: null,
            company: null,
            preview: emptyPreview,
        })
    }, [])

    return {
        state,
        create: createMutation.mutateAsync,
        upload: uploadMutation.mutateAsync,
        resetSheets: resetMutation.mutateAsync,
        complete: completeMutation.mutateAsync,
        resetWizard: reset,
        isCreating: createMutation.isLoading,
        isUploading: uploadMutation.isLoading,
        isResetting: resetMutation.isLoading,
        isCompleting: completeMutation.isLoading,
    }
}
